// M040 C2: hardcoded colors → tokens, text-* → text-tok-*
/**
 * DecisionFlow — PositionDataReview
 * data-fit LLC · Confidential
 * Stage 3: DATA SETUP — Screen SETUP-01
 * Spec authority: ComponentSpec_DATASETUP_v1_corrected.md (Session 90)
 * MasterContext authority: MasterContext_Session90_Close.md
 * Generated: Session 90 — ChatGPT Phase 3
 * Path: ui/src/components/tenant-admin/PositionDataReview.jsx
 *
 * OPEN ITEMS IN THIS FILE:
 * OI-SETUP-POSITION-ENDPOINT-CONFIRM
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthContext.jsx';
import { API_BASE } from './DataSetupFlowShell';

const EMPTY_FORM = {
  position_id: null,
  person_uuid: '',
  full_name: '',
  role_id: '',
  segment_id: '',
  region_id: '',
  sub_region_id: '',
  is_active: true
};

const EMPTY_FILTERS = {
  role_id: '',
  segment_id: '',
  is_active: ''
};

function toIntegerOrNull(value) {
  if (value === '' || value === null || value === undefined) {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeBooleanFilter(value) {
  if (value === '') return '';
  return value === 'true';
}

function getErrorMessage(error, fallback) {
  return error instanceof Error && error.message ? error.message : fallback;
}

function PositionDataReview({ tier = 'essentials', onBack, onNext }) {
  const { user } = useAuth();

  const [positions, setPositions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [segments, setSegments] = useState([]);
  const [regions, setRegions] = useState([]);
  const [subRegions, setSubRegions] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [referenceLoading, setReferenceLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [inlineError, setInlineError] = useState('');
  const [blockingError, setBlockingError] = useState('');
  const [toast, setToast] = useState(null);

  const isEnterprise = tier === 'enterprise';
  const isEditing = editingId !== null;

  // ── CSV bulk-import state (matches QuotaDataReview pattern · OI-SETUP-POSITION-ENDPOINT-CONFIRM) ──
  const [csvFile, setCsvFile]           = useState(null);
  const [csvDryResult, setCsvDryResult] = useState(null);
  const [csvCommitted, setCsvCommitted] = useState(false);
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvError, setCsvError]         = useState('');

  const showToast = useCallback((message) => {
    setToast(message);
    window.setTimeout(() => {
      setToast(null);
    }, 3000);
  }, []);

  const fetchJson = useCallback(
    async (url, options = {}) => {
      const response = await fetch(url, {
        ...options,
        credentials: 'include',
        headers: {
          ...(options.headers || {})
        }
      });

      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || `Request failed with status ${response.status}`);
      }

      if (response.status === 204) {
        return null;
      }

      return response.json();
    },
    []
  );

  const buildPositionQuery = useCallback(() => {
    const params = new URLSearchParams();

    // OI-SETUP-POSITION-ENDPOINT-CONFIRM: confirm query params for GET /positions filters by role_id, segment_id, is_active
    if (filters.role_id) {
      params.set('role_id', String(toIntegerOrNull(filters.role_id)));
    }

    if (filters.segment_id) {
      params.set('segment_id', String(toIntegerOrNull(filters.segment_id)));
    }

    if (filters.is_active !== '') {
      params.set('is_active', String(normalizeBooleanFilter(filters.is_active)));
    }

    const queryString = params.toString();
    return `${API_BASE}/positions${queryString ? `?${queryString}` : ''}`;
  }, [filters]);

  const loadPositions = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    setInlineError('');

    try {
      const data = await fetchJson(buildPositionQuery());
      setPositions(Array.isArray(data) ? data : data?.positions || []);
    } catch (error) {
      setInlineError(getErrorMessage(error, 'Unable to fetch positions.'));
    } finally {
      setLoading(false);
    }
  }, [buildPositionQuery, fetchJson, user]);

  const loadReferenceData = useCallback(async () => {
    if (!user) return;

    setReferenceLoading(true);
    setBlockingError('');

    try {
      const [rolesData, segmentsData] = await Promise.all([
        fetchJson(`${API_BASE}/roles`),
        fetchJson(`${API_BASE}/segments`),
      ]);

      setRoles(Array.isArray(rolesData) ? rolesData : rolesData?.roles || []);
      setSegments(
        Array.isArray(segmentsData) ? segmentsData : segmentsData?.segments || []
      );

      // Regions are optional — endpoint may not exist; fail silently
      if (isEnterprise) {
        try {
          const regionsData = await fetchJson(`${API_BASE}/regions`);
          setRegions(Array.isArray(regionsData) ? regionsData : regionsData?.regions || []);
        } catch {
          setRegions([]);
        }
      }
    } catch (error) {
      setBlockingError(
        getErrorMessage(error, 'Unable to fetch required position reference data.')
      );
    } finally {
      setReferenceLoading(false);
    }
  }, [fetchJson, isEnterprise, user]);

  const loadSubRegions = useCallback(
    async (regionId) => {
      if (!user || !isEnterprise || !regionId) {
        setSubRegions([]);
        return;
      }

      try {
        const parsedRegionId = toIntegerOrNull(regionId);
        const data = await fetchJson(
          `${API_BASE}/sub-regions?region_id=${encodeURIComponent(parsedRegionId)}`
        );
        setSubRegions(Array.isArray(data) ? data : data?.sub_regions || []);
      } catch (error) {
        setInlineError(getErrorMessage(error, 'Unable to fetch sub-regions.'));
      }
    },
    [fetchJson, isEnterprise, user]
  );

  useEffect(() => {
    loadReferenceData();
  }, [loadReferenceData]);

  useEffect(() => {
    loadPositions();
  }, [loadPositions]);

  useEffect(() => {
    if (isEnterprise && form.region_id) {
      loadSubRegions(form.region_id);
    } else {
      setSubRegions([]);
    }
  }, [form.region_id, isEnterprise, loadSubRegions]);

  const updateFilter = useCallback((field, value) => {
    setFilters((previous) => ({
      ...previous,
      [field]: value
    }));
  }, []);

  const updateForm = useCallback((field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
      ...(field === 'region_id' ? { sub_region_id: '' } : {})
    }));
  }, []);

  // ── CSV bulk-import handlers (OI-SETUP-POSITION-ENDPOINT-CONFIRM) ───────────
  // source_system = direct_api · file_type = position
  // Column mapping auto-seeded on first confirm (idempotent upsert).
  // S469 Step B — UnifiedImportSpec_v1.md §3.1 POSITION_REQUIRED_COLS correction.
  // person_uuid removed — server-generated on INSERT, never customer-supplied (NN-38).
  // role_id / segment_id replaced with human-readable role_code / segment_code.
  // hire_date and is_active added — required per spec §3.1.
  const POSITION_REQUIRED_COLS = ['employee_id', 'full_name', 'role_code', 'segment_code', 'hire_date', 'is_active'];

  const ensurePositionColumnMapping = useCallback(async () => {
    // S469 Step B — canonical human-readable codes. Server resolves to internal IDs.
    // person_uuid / position_id never in mapping — server-generated (NN-38).
    const mappings = [
      { source_column_name: 'employee_id',         target_column_name: 'employee_id',   is_required: true  },
      { source_column_name: 'full_name',            target_column_name: 'full_name',     is_required: true  },
      { source_column_name: 'role_code',            target_column_name: 'role_code',     is_required: true  },
      { source_column_name: 'segment_code',         target_column_name: 'segment_code',  is_required: true  },
      { source_column_name: 'hire_date',            target_column_name: 'hire_date',     is_required: true  },
      { source_column_name: 'is_active',            target_column_name: 'is_active',     is_required: true  },
      { source_column_name: 'ramp_start_date',      target_column_name: 'ramp_start_date', is_required: false },
      { source_column_name: 'territory_code',       target_column_name: 'territory_code',  is_required: false },
      { source_column_name: 'manager_employee_id',  target_column_name: 'manager_employee_id', is_required: false },
      { source_column_name: 'email',                target_column_name: 'email',         is_required: false },
      { source_column_name: 'country_code',         target_column_name: 'country_code',  is_required: false },
      { source_column_name: 'business_unit_code',   target_column_name: 'business_unit_code', is_required: false },
    ];
    const res = await fetch(`${API_BASE}/import/column-mapping`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ source_system: 'direct_api', file_type: 'position', mappings }),
    });
    if (!res.ok) throw new Error('Failed to seed position column mapping.');
  }, []);

  const runCsvValidate = useCallback(async (file) => {
    if (!file) return;
    setCsvImporting(true);
    setCsvError('');
    setCsvDryResult(null);
    setCsvCommitted(false);
    try {
      const text = await file.text();
      const lines = text.trim().split('\n');
      if (lines.length < 2) { setCsvError('CSV has no data rows.'); return; }
      const headers = lines[0].split(',').map((h) => h.trim());
      const missing = POSITION_REQUIRED_COLS.filter((c) => !headers.includes(c));
      if (missing.length > 0) {
        setCsvError(`Missing required columns: ${missing.join(', ')}`);
        return;
      }
      const warnings = [];
      if (!headers.includes('ramp_start_date'))     warnings.push('ramp_start_date absent — ramp will not be set.');
      if (!headers.includes('territory_code'))      warnings.push('territory_code absent — territory will not be set.');
      if (!headers.includes('manager_employee_id')) warnings.push('manager_employee_id absent — manager will not be linked.');
      if (!headers.includes('email'))               warnings.push('email absent — email will not be set.');
      setCsvDryResult({ rows: lines.length - 1, warnings, errors: [] });
    } catch { setCsvError('Failed to parse CSV.'); }
    finally { setCsvImporting(false); }
  }, []);

  const runCsvCommit = useCallback(async (file) => {
    if (!file || !csvDryResult) return;
    setCsvImporting(true);
    setCsvError('');
    try {
      await ensurePositionColumnMapping();
      const fd = new FormData();
      fd.append('file', file);
      fd.append('source_system', 'direct_api');
      fd.append('file_type', 'position');
      // OI-SETUP-POSITION-ENDPOINT-CONFIRM: confirm /import/upload accepts file_type=position
      const res = await fetch(`${API_BASE}/import/upload`, { method: 'POST', credentials: 'include', body: fd });
      const json = await res.json();
      if (!res.ok) { setCsvError(json?.message || json?.detail || `Error ${res.status}`); return; }
      setCsvCommitted(true);
      setCsvDryResult(null);
      setCsvFile(null);
      showToast(`${csvDryResult.rows} position(s) loaded.`);
      await loadPositions();
    } catch (e) { setCsvError(e.message || 'Commit failed.'); }
    finally { setCsvImporting(false); }
  }, [csvDryResult, ensurePositionColumnMapping, loadPositions, showToast]);

  const resetForm = useCallback(() => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setInlineError('');
  }, []);

  const startEdit = useCallback((position) => {
    setEditingId(position.position_id);

    setForm({
      position_id: position.position_id ?? null,
      person_uuid: position.person_uuid ?? '',
      full_name: position.full_name ?? '',
      role_id: position.role_id ? String(position.role_id) : '',
      segment_id: position.segment_id ? String(position.segment_id) : '',
      region_id: position.region_id ? String(position.region_id) : '',
      sub_region_id: position.sub_region_id ? String(position.sub_region_id) : '',
      is_active:
        typeof position.is_active === 'boolean' ? position.is_active : true
    });

    setInlineError('');
  }, []);

  const validateForm = useCallback(() => {
    // person_uuid is server-generated on INSERT — never validated as customer input (NN-38).
    if (!form.full_name.trim()) {
      return 'Full name is required.';
    }

    if (!form.role_id) {
      return 'Role is required.';
    }

    if (!form.segment_id) {
      return 'Segment is required.';
    }

    return '';
  }, [form, isEditing]);

  const buildCreatePayload = useCallback(() => {
    // person_uuid omitted — server-generated on INSERT, immutable after (NN-38).
    const payload = {
      full_name: form.full_name.trim(),
      role_id: toIntegerOrNull(form.role_id),
      segment_id: toIntegerOrNull(form.segment_id),
      is_active: Boolean(form.is_active)
    };

    if (isEnterprise) {
      payload.region_id = toIntegerOrNull(form.region_id);
      payload.sub_region_id = toIntegerOrNull(form.sub_region_id);
    }

    return payload;
  }, [form, isEnterprise]);

  const buildUpdatePayload = useCallback(() => {
    const payload = {
      full_name: form.full_name.trim(),
      role_id: toIntegerOrNull(form.role_id),
      segment_id: toIntegerOrNull(form.segment_id),
      is_active: Boolean(form.is_active)
    };

    if (isEnterprise) {
      payload.region_id = toIntegerOrNull(form.region_id);
      payload.sub_region_id = toIntegerOrNull(form.sub_region_id);
    }

    return payload;
  }, [form, isEnterprise]);

  const savePosition = useCallback(
    async (event) => {
      event.preventDefault();

      if (!user) {
        setInlineError('Authentication is required to save positions.');
        return;
      }

      const validationMessage = validateForm();
      if (validationMessage) {
        setInlineError(validationMessage);
        return;
      }

      setSaving(true);
      setInlineError('');

      try {
        if (isEditing) {
          const updated = await fetchJson(`${API_BASE}/positions/${editingId}`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(buildUpdatePayload())
          });

          setPositions((previous) =>
            previous.map((position) =>
              position.position_id === editingId
                ? { ...position, ...(updated || buildUpdatePayload()) }
                : position
            )
          );

          showToast('Position updated.');
        } else {
          const created = await fetchJson(`${API_BASE}/positions`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(buildCreatePayload())
          });

          if (created) {
            setPositions((previous) => [created, ...previous]);
          } else {
            await loadPositions();
          }

          showToast('Position created.');
        }

        resetForm();
      } catch (error) {
        setInlineError(getErrorMessage(error, 'Unable to save position.'));
      } finally {
        setSaving(false);
      }
    },
    [
      buildCreatePayload,
      buildUpdatePayload,
      editingId,
      fetchJson,
      isEditing,
      loadPositions,
      resetForm,
      showToast,
      user,
      validateForm
    ]
  );

  const roleNameById = useMemo(() => {
    return roles.reduce((accumulator, role) => {
      accumulator[String(role.role_id)] = role.role_name || role.name;
      return accumulator;
    }, {});
  }, [roles]);

  const segmentNameById = useMemo(() => {
    return segments.reduce((accumulator, segment) => {
      accumulator[String(segment.segment_id)] =
        segment.segment_name || segment.name;
      return accumulator;
    }, {});
  }, [segments]);

  const regionNameById = useMemo(() => {
    return regions.reduce((accumulator, region) => {
      accumulator[String(region.region_id)] = region.region_name || region.name;
      return accumulator;
    }, {});
  }, [regions]);

  const subRegionNameById = useMemo(() => {
    return subRegions.reduce((accumulator, subRegion) => {
      accumulator[String(subRegion.sub_region_id)] =
        subRegion.sub_region_name || subRegion.name;
      return accumulator;
    }, {});
  }, [subRegions]);

  const inputClass   = 'mt-1 block w-full rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] text-[rgb(var(--color-text))] px-3 py-2 text-tok-sm focus:outline-none';
  const tdClass      = 'px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] border-b border-[rgb(var(--color-border))]';
  const btnPrimary   = 'rounded-lg bg-[rgb(var(--color-primary))] px-3 py-1.5 text-tok-sm font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50 disabled:cursor-not-allowed';
  const btnSecondary = 'rounded-lg bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] px-3 py-1.5 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50 disabled:cursor-not-allowed';

  if (!user) {
    return (
      <section className="p-6">
        <h2 className="text-tok-xl font-semibold mb-4">Position Data Review</h2>
        <div role="alert" className="rounded-md bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 text-[rgb(var(--color-danger))] p-4">
          Authentication is required to review position data.
        </div>
      </section>
    );
  }

  if (blockingError) {
    return (
      <section className="p-6">
        <h2 className="text-tok-xl font-semibold mb-4">Position Data Review</h2>
        <div role="alert" className="rounded-md bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 text-[rgb(var(--color-danger))] p-4 mb-4">
          {blockingError}
        </div>
        <button type="button" onClick={loadReferenceData} className="px-4 py-2 rounded bg-[rgb(var(--color-primary))] text-white text-tok-sm font-medium hover:bg-[rgb(var(--color-primary-hover))]">
          Retry
        </button>
      </section>
    );
  }

  return (
    <section className="space-y-6">
      <header className="flex items-start justify-between">
        <div>
          <p className="text-tok-xs font-semibold uppercase tracking-widest text-[rgb(var(--color-primary))] mb-1">SETUP-01</p>
          <h2 className="text-tok-2xl font-bold text-[rgb(var(--color-text))]">Position Data Review</h2>
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))] mt-1">
            Review imported positions and add or update position records before
            plan assignment.
          </p>
        </div>
      </header>

      {toast ? (
        <div role="status" className="rounded-md bg-[rgb(var(--color-success))]/10 border border-[rgb(var(--color-success))]/30 text-[rgb(var(--color-success))] px-4 py-3 text-tok-sm">
          {toast}
        </div>
      ) : null}

      {inlineError ? (
        <div role="alert" className="rounded-md bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 text-[rgb(var(--color-danger))] px-4 py-3 text-tok-sm">
          {inlineError}
        </div>
      ) : null}

      {/* ── CSV Bulk Import (matches QuotaDataReview · OI-SETUP-POSITION-ENDPOINT-CONFIRM) ── */}
      <div className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 flex flex-col gap-5 shadow-sm">
        <div>
          <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">CSV Bulk Import</h3>
          <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
            Upload <strong>Positions.csv</strong> to load position records in one pass. Validates before committing — review results before confirming.
          </p>
        </div>

        {csvError ? (
          <div role="alert" className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
            {csvError}
          </div>
        ) : null}

        <div className="rounded-lg border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] p-4 flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[rgb(var(--color-primary))] text-tok-xs font-bold text-white">1</span>
            <span className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
              Positions.csv — position rows
              {csvCommitted ? <span className="ml-2 text-[rgb(var(--color-success))] font-normal">✓ Loaded</span> : null}
            </span>
          </div>

          <p className="text-tok-xs text-[rgb(var(--color-text-muted))]">
            Required columns: <code>employee_id, full_name, role_code, segment_code, hire_date, is_active</code>
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <input
              type="file"
              accept=".csv"
              className="text-tok-sm text-[rgb(var(--color-text))] file:mr-3 file:rounded-lg file:border-0 file:bg-[rgb(var(--color-primary))] file:px-3 file:py-1.5 file:text-tok-sm file:font-medium file:text-white hover:file:opacity-90"
              onChange={(e) => {
                const f = e.target.files?.[0] || null;
                setCsvFile(f);
                setCsvDryResult(null);
                setCsvCommitted(false);
                setCsvError('');
              }}
            />
            <button
              type="button"
              className={btnSecondary}
              disabled={!csvFile || csvImporting}
              onClick={() => runCsvValidate(csvFile)}
            >
              {csvImporting && !csvDryResult ? 'Validating…' : 'Validate'}
            </button>
          </div>

          {csvDryResult ? (
            <div className="mt-1 flex flex-col gap-2">
              <div className="text-tok-sm text-[rgb(var(--color-text-muted))]">
                Rows parsed: <strong>{csvDryResult.rows}</strong> · Positions to load: <strong>{csvDryResult.rows}</strong>
              </div>
              {csvDryResult.warnings?.map((w, i) => (
                <p key={i} className="text-tok-xs text-amber-600">⚠ {w}</p>
              ))}
              {csvDryResult.errors?.length > 0 ? (
                <p className="text-tok-sm text-[rgb(var(--color-danger))]">{csvDryResult.errors.length} error(s) — fix CSV and re-upload.</p>
              ) : (
                <div className="flex items-center gap-3">
                  <span className="text-tok-sm text-[rgb(var(--color-success))]">
                    ✓ Validation passed — {csvDryResult.rows} position(s) ready to load.
                  </span>
                  <button
                    type="button"
                    className={btnPrimary}
                    disabled={csvImporting}
                    onClick={() => runCsvCommit(csvFile)}
                  >
                    {csvImporting ? 'Loading…' : 'Confirm and load'}
                  </button>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>

      <section className="bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] rounded-lg p-6">
        <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))] mb-4">Filters</h3>
        <div className="grid grid-cols-3 gap-4">
          <label>
            Role
            <select
              className={inputClass}
              value={filters.role_id}
              onChange={(event) => updateFilter('role_id', event.target.value)}
              disabled={referenceLoading}
            >
              <option value="">All roles</option>
              {roles.map((role) => (
                <option key={role.role_id} value={role.role_id}>
                  {role.role_name || role.name}
                </option>
              ))}
            </select>
          </label>

          <label>
            Segment
            <select
              className={inputClass}
              value={filters.segment_id}
              onChange={(event) =>
                updateFilter('segment_id', event.target.value)
              }
              disabled={referenceLoading}
            >
              <option value="">All segments</option>
              {segments.map((segment) => (
                <option key={segment.segment_id} value={segment.segment_id}>
                  {segment.segment_name || segment.name}
                </option>
              ))}
            </select>
          </label>

          <label>
            Status
            <select
              className={inputClass}
              value={filters.is_active}
              onChange={(event) =>
                updateFilter('is_active', event.target.value)
              }
            >
              <option value="">All statuses</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </label>
        </div>

        <div className="mt-4 flex gap-2">
          <button type="button" onClick={loadPositions} disabled={loading} className="px-4 py-2 rounded bg-[rgb(var(--color-primary))] text-white text-tok-sm font-medium hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50">
            {loading ? 'Refreshing…' : 'Refresh positions'}
          </button>
        </div>
      </section>

      <section className="bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] rounded-lg p-6">
        <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))] mb-4">{isEditing ? 'Edit position' : 'Add position'}</h3>

        <form onSubmit={savePosition}>
          <div className="grid grid-cols-2 gap-4">
            <label>
              {/* person_uuid — read-only display only. Server-generated on INSERT,
                  immutable after (NN-38). Never accepted from customer input on any
                  surface. Shows dashes on create until server assigns. */}
              person_uuid
              <span className="block mt-1 text-tok-sm text-[rgb(var(--color-text))] font-mono">
                {form.person_uuid || <span className="text-[rgb(var(--color-text-muted))] italic">assigned by server</span>}
              </span>
            </label>

            <label>
              Full name
              <input
                type="text"
                className={inputClass}
                  value={form.full_name}
                onChange={(event) => updateForm('full_name', event.target.value)}
                required
              />
            </label>

            <label>
              Role
              <select
                className={inputClass}
              value={form.role_id}
                onChange={(event) => updateForm('role_id', event.target.value)}
                required
              >
                <option value="">Select role</option>
                {roles.map((role) => (
                  <option key={role.role_id} value={role.role_id}>
                    {role.role_name || role.name}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Segment
              <select
                className={inputClass}
              value={form.segment_id}
                onChange={(event) =>
                  updateForm('segment_id', event.target.value)
                }
                required
              >
                <option value="">Select segment</option>
                {segments.map((segment) => (
                  <option key={segment.segment_id} value={segment.segment_id}>
                    {segment.segment_name || segment.name}
                  </option>
                ))}
              </select>
            </label>

            {isEnterprise ? (
              <>
                <label>
                  Region
                  <select
                    className={inputClass}
              value={form.region_id}
                    onChange={(event) =>
                      updateForm('region_id', event.target.value)
                    }
                  >
                    <option value="">No region</option>
                    {regions.map((region) => (
                      <option key={region.region_id} value={region.region_id}>
                        {region.region_name || region.name}
                      </option>
                    ))}
                  </select>
                </label>

                <label>
                  Sub-region
                  <select
                    className={inputClass}
              value={form.sub_region_id}
                    onChange={(event) =>
                      updateForm('sub_region_id', event.target.value)
                    }
                    disabled={!form.region_id}
                  >
                    <option value="">No sub-region</option>
                    {subRegions.map((subRegion) => (
                      <option
                        key={subRegion.sub_region_id}
                        value={subRegion.sub_region_id}
                      >
                        {subRegion.sub_region_name || subRegion.name}
                      </option>
                    ))}
                  </select>
                </label>
              </>
            ) : null}

            <label>
              Active
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(event) =>
                  updateForm('is_active', event.target.checked)
                }
              />
            </label>
          </div>

          <div className="mt-4 flex gap-2">
            <button type="submit" disabled={saving} className="px-4 py-2 rounded bg-[rgb(var(--color-primary))] text-white text-tok-sm font-medium hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50">
              {saving ? 'Saving…' : isEditing ? 'Save changes' : 'Create position'}
            </button>
            <button type="button" onClick={resetForm} disabled={saving} className="px-4 py-2 rounded border border-[rgb(var(--color-border))] text-[rgb(var(--color-text))] text-tok-sm font-medium hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50">
              Clear
            </button>
          </div>
        </form>
      </section>

      <section className="bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-tok-base font-semibold text-[rgb(var(--color-text))]">Positions</h3>
          {loading ? <span className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading…</span> : null}
        </div>

        {positions.length === 0 && !loading ? (
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))] italic">No positions match the selected filters.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-[rgb(var(--color-border))] text-tok-sm">
              <thead>
                <tr>
                  <th>person_uuid</th>
                  <th>Full name</th>
                  <th>Role</th>
                  <th>Segment</th>
                  {isEnterprise ? <th>Region</th> : null}
                  {isEnterprise ? <th>Sub-region</th> : null}
                  <th>Status</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {positions.map((position) => (
                  <tr key={position.position_id || position.person_uuid}>
                    <td className={tdClass}>
                      <code>{position.person_uuid}</code>
                    </td>
                    <td className={tdClass}>{position.full_name}</td>
                    <td className={tdClass}>{roleNameById[String(position.role_id)] || position.role_id}</td>
                    <td className={tdClass}>
                      {segmentNameById[String(position.segment_id)] ||
                        position.segment_id}
                    </td>
                    {isEnterprise ? (
                      <td className={tdClass}>
                        {regionNameById[String(position.region_id)] ||
                          position.region_id ||
                          '—'}
                      </td>
                    ) : null}
                    {isEnterprise ? (
                      <td className={tdClass}>
                        {subRegionNameById[String(position.sub_region_id)] ||
                          position.sub_region_id ||
                          '—'}
                      </td>
                    ) : null}
                    <td className={tdClass}>{position.is_active ? 'Active' : 'Inactive'}</td>
                    <td className={tdClass}>
                      <button type="button" onClick={() => startEdit(position)}>
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <footer className="flex justify-between pt-4 border-t border-[rgb(var(--color-border))]">
        <button type="button" onClick={onBack} className="px-4 py-2 rounded border border-[rgb(var(--color-border))] text-[rgb(var(--color-text))] text-tok-sm font-medium hover:bg-[rgb(var(--color-surface-muted))]">
          Back
        </button>
        <button type="button" onClick={onNext} className="px-4 py-2 rounded bg-[rgb(var(--color-primary))] text-white text-tok-sm font-medium hover:bg-[rgb(var(--color-primary-hover))]">
          Continue to quotas
        </button>
      </footer>
    </section>
  );
}

export default PositionDataReview;
